import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X } from "lucide-react";

const ACCENT = "#d04260";

// Mappa dei demo disponibili -> URL interno + titolo
const DEMOS = {
  "welcome-book": {
    src: "/guestbook-v3/prop_demo_hostland?embed=1&showcase=1",
    title: "Welcome Book · Demo live",
  },
  site: {
    src: "/property/prop_demo_hostland?embed=1",
    title: "Landing · Demo live",
  },
};

// Larghezza logica reale di uno smartphone: l'iframe carica la vera app a 390px
// e viene scalato per riempire lo schermo del mockup.
const DEVICE_W = 390;
const DEVICE_H = Math.round((DEVICE_W * 956) / 440);

export default function LiveDemoViewer() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const demo = DEMOS[kind] || DEMOS["welcome-book"];
  const fromHostlander =
    new URLSearchParams(window.location.search).get("from") === "hostlander";

  const screenRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / DEVICE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const closeViewer = () => {
    // Aperto in una nuova scheda: prova a chiuderla, altrimenti torna alla home
    window.close();
    setTimeout(() => { if (!window.closed) navigate("/"); }, 120);
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden"
      style={{ background: "radial-gradient(120% 90% at 50% 40%, #14141a 0%, #000 70%)" }}
      data-testid="live-demo-viewer"
    >
      {/* Glow accento dietro al telefono */}
      <div
        aria-hidden
        className="pointer-events-none absolute w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
        style={{ background: `radial-gradient(circle, ${ACCENT}66, transparent 65%)` }}
      />

      {/* Pulsante chiudi */}
      <button
        onClick={closeViewer}
        aria-label="Chiudi"
        data-testid="live-demo-close"
        className="absolute top-5 right-5 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white backdrop-blur-md border border-white/15 transition-colors hover:bg-white/20"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Wordmark + hint */}
      <div className="absolute top-6 left-6 z-20 flex flex-col gap-1 select-none">
        <span className="text-white font-black tracking-tight text-lg leading-none">Hostland</span>
        <span className="text-white/45 text-[11px] font-bold uppercase tracking-[0.18em]">{demo.title}</span>
      </div>

      {/* Phone mockup — centrato, riempie l'altezza dello schermo */}
      <div
        className="relative"
        style={{ height: "min(92vh, 920px)", aspectRatio: "440/956" }}
        data-testid="live-demo-phone"
      >
        {/* chassis titanio */}
        <div
          className="absolute inset-0 rounded-[54px] p-[12px] sm:p-[14px]"
          style={{
            background: "linear-gradient(150deg,#42424a 0%,#0a0a0c 46%,#2a2a30 100%)",
            boxShadow:
              `0 40px 90px -20px ${ACCENT}40, 0 60px 160px -50px rgba(0,0,0,0.8), inset 0 1.5px 0 rgba(255,255,255,0.16), inset 0 0 0 1.5px rgba(255,255,255,0.05)`,
          }}
        >
          {/* pulsanti laterali */}
          <div aria-hidden className="absolute -left-[3px] top-[22%] w-[3px] h-9 rounded-l bg-zinc-700/90" />
          <div aria-hidden className="absolute -left-[3px] top-[33%] w-[3px] h-14 rounded-l bg-zinc-700/90" />
          <div aria-hidden className="absolute -right-[3px] top-[27%] w-[3px] h-20 rounded-r bg-zinc-700/90" />

          <div ref={screenRef} className="relative w-full h-full rounded-[44px] overflow-hidden bg-black">
            {/* dynamic island */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-[30%] h-6 rounded-full bg-black flex items-center justify-end px-2.5 shadow-inner">
              <span className="w-2 h-2 rounded-full bg-zinc-900 ring-1 ring-zinc-700/70" />
            </div>

            {scale <= 0 && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-zinc-50 to-white">
                <div className="w-7 h-7 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">Demo live</span>
              </div>
            )}
            {scale > 0 && (
              <div
                className="absolute top-0 left-0"
                style={{ width: DEVICE_W, height: DEVICE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
              >
                <iframe
                  src={demo.src}
                  title={demo.title}
                  referrerPolicy="no-referrer"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  className="border-0 bg-white block"
                  style={{ width: DEVICE_W, height: DEVICE_H }}
                  data-testid="live-demo-iframe"
                />
              </div>
            )}

            {/* riflesso vetro */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none z-20"
              style={{ background: "linear-gradient(120deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 26%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.06) 100%)" }}
            />
          </div>
        </div>
      </div>

      {/* Hint tattile in basso / Avvertenza "genera dipendenza" da Hostlander */}
      {fromHostlander ? (
        <div
          className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 w-[min(92vw,540px)] px-2"
          data-testid="live-demo-addiction-warning"
        >
          <div
            className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 rounded-2xl border-2 px-4 py-3.5 backdrop-blur-md"
            style={{
              background: "rgba(10,10,12,0.72)",
              borderColor: `${ACCENT}`,
              boxShadow: `0 20px 60px -20px ${ACCENT}66`,
            }}
          >
            <div className="flex-1 text-left">
              <span
                className="text-[10px] font-black uppercase tracking-[0.18em]"
                style={{ color: "#F9D23C" }}
              >
                ⚠️ Avvertenza
              </span>
              <p className="text-white text-[13px] leading-snug font-bold mt-0.5">
                Hostland genera dipendenza. L'uso prolungato rende impossibile
                tornare alle vecchie piattaforme. 😵‍💫
              </p>
            </div>
            <a
              href="/hostlander"
              data-testid="live-demo-back-to-hostlander"
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[14px] font-black text-white transition-transform active:scale-95 hover:brightness-110"
              style={{ background: ACCENT }}
            >
              Vuoi provarlo 3 mesi? 🔑
            </a>
          </div>
        </div>
      ) : (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/80 text-[12px] font-bold backdrop-blur-md border border-white/10">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
            </span>
            Scorri e tocca — è la vera app
          </span>
        </div>
      )}
    </div>
  );
}
